/**
 * PostHog telemetry helpers for the travel planner UI.
 *
 * Mirrors the event names emitted by the backend (`api_search/lib/planner/telemetry.py`)
 * so dashboards can union both sources without renames. The two FE-only events
 * are `planner_user_abort` (the user clicked Stop or closed the tab mid-stream)
 * and `planner_frontend_error` (the SSE connection or chat fetch failed).
 *
 * GON-5364 — see thoughts/shared/research/2026-04-30-trip-planner-v2-hardened-chat-loop.md
 */

import posthog from "posthog-js";

export const PLANNER_EVENT = {
  USER_ABORT: "planner_user_abort",
  FRONTEND_ERROR: "planner_frontend_error",
} as const;

const SERVICE_NAME = "gondola-web-app";

const safeCapture = (
  event: string,
  properties: Record<string, unknown>
): void => {
  try {
    posthog.capture(event, { ...properties, service: SERVICE_NAME });
  } catch {
    // Telemetry must NEVER break the planner UI.
  }
};

export interface PlannerUserAbortProps {
  session_id: string | null;
  /** Number of streamed events (text/tool_call/tool_result) before the abort. */
  events_received: number;
  /** ISO timestamp from the moment the user pressed Stop. */
  aborted_at: string;
}

export const capturePlannerUserAbort = (props: PlannerUserAbortProps): void => {
  safeCapture(PLANNER_EVENT.USER_ABORT, { ...props });
};

export interface PlannerFrontendErrorProps {
  session_id: string | null;
  /** Where in the UI the error happened — kept stable for dashboard filters. */
  location:
    | "send_message"
    | "load_session"
    | "load_sessions"
    | "delete_session"
    | "create_review_session";
  /** Best-effort error message — may be empty. */
  message: string;
  /** HTTP status if the failure was a non-OK response, else undefined. */
  status?: number;
}

export const capturePlannerFrontendError = (
  props: PlannerFrontendErrorProps
): void => {
  safeCapture(PLANNER_EVENT.FRONTEND_ERROR, { ...props });
};
